import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSesion } from '../estado/SesionContext';
import type { Premio } from '../domain/types';

export function Recompensas() {
  const { repo, espacio, puntos, refrescarPuntos } = useSesion();
  const [premios, setPremios] = useState<Premio[] | null>(null);
  const [aviso, setAviso] = useState('');
  const [pidiendo, setPidiendo] = useState<string | null>(null);

  /**
   * Pide el premio. El servidor valida el saldo —descontando lo ya comprometido en
   * solicitudes abiertas— y crea la solicitud en estado `solicitado`; la aprueba un admin
   * desde Treid OP. Si el repo devuelve `false` es que todavia no hay backend: se dice,
   * no se finge una solicitud que nadie va a ver.
   */
  async function pedir(premio: Premio) {
    if (!repo || pidiendo) return;
    setPidiendo(premio.id);
    setAviso('');
    try {
      const ok = await repo.solicitarCanje(premio.id);
      setAviso(
        ok
          ? `Solicitaste «${premio.nombre}». Queda pendiente de la aprobación de tu supervisor.`
          : `El canje de «${premio.nombre}» todavía no está disponible en esta versión.`,
      );
      await refrescarPuntos();
    } catch (e) {
      setAviso(e instanceof Error ? e.message : 'No se pudo solicitar el canje.');
    } finally {
      // Siempre: si no, el boton queda muerto tras el primer error.
      setPidiendo(null);
    }
  }

  useEffect(() => {
    if (!repo) return;
    let vivo = true;
    void repo.listarPremios(espacio.id).then((p) => {
      if (vivo) setPremios(p);
    });
    return () => {
      vivo = false;
    };
  }, [repo, espacio.id]);

  return (
    <div>
      <Link to="/" className="text-sm font-bold text-azul">
        ← Inicio
      </Link>

      <h1 className="mt-3 font-titulos text-2xl font-extrabold text-tinta">Recompensas</h1>
      <p className="text-sm text-gris">
        {premios !== null && premios.length === 0
          ? 'Tus puntos se acumulan desde ahora'
          : 'Canjea tus puntos por incentivos reales'}
      </p>

      <section className="mt-5 flex flex-col gap-3 rounded-2xl bg-gradient-to-br from-azul to-azul-osc px-6 py-5 text-white sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-[0.65rem] font-extrabold tracking-widest opacity-85">
            TU SALDO ACTUAL
          </p>
          <p className="font-titulos text-3xl font-extrabold">⭐ {puntos} pts</p>
        </div>
        <p className="text-xs leading-relaxed opacity-90 sm:max-w-xs sm:text-right">
          Ganas puntos aprobando cursos.
          <br />
          Bonus de +50 por nota perfecta al primer intento.
        </p>
      </section>

      <div className="mt-6 space-y-3">
        {premios === null ? (
          <p className="py-6 text-center text-gris">Cargando…</p>
        ) : premios.length === 0 ? (
          /*
           * Sin premios activos la pantalla NO muestra una lista vacía: dice que el catálogo
           * está por venir. Es el estado real mientras el cliente aprueba qué se va a entregar,
           * y se enciende solo, activando los premios desde Treid OP, sin desplegar nada.
           *
           * El saldo se sigue mostrando a propósito: los puntos se están ganando igual, y
           * esconderlos haría pensar que aprobar cursos no sirve de nada.
           */
          <section className="rounded-2xl border border-borde bg-white px-6 py-10 text-center">
            <p className="text-4xl">🎁</p>
            <p className="mt-3 font-titulos text-lg font-extrabold text-tinta">
              Catálogo próximamente
            </p>
            <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-gris">
              Estamos cerrando los premios de esta campaña. Mientras tanto,{' '}
              <b>tus puntos se siguen acumulando</b> con cada curso que apruebas: cuando el
              catálogo abra, los vas a poder canjear.
            </p>
          </section>
        ) : (
          premios.map((premio) => {
            const alcanza = puntos >= premio.puntos;
            return (
              <article
                key={premio.id}
                className="flex items-center gap-4 rounded-2xl border border-borde bg-white p-4"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-cielo text-2xl">
                  {premio.icono}
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="font-extrabold leading-tight">{premio.nombre}</h3>
                  <p className="text-xs text-gris">{premio.detalle}</p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-extrabold text-azul">{premio.puntos} pts</p>
                  <button
                    disabled={!alcanza || pidiendo === premio.id}
                    onClick={() => void pedir(premio)}
                    className="mt-1 rounded-full bg-rojo px-4 py-1.5 text-xs font-extrabold text-white disabled:bg-borde disabled:text-gris"
                  >
                    {pidiendo === premio.id ? '…' : alcanza ? 'CANJEAR' : `FALTAN ${premio.puntos - puntos}`}
                  </button>
                </div>
              </article>
            );
          })
        )}
      </div>

      {aviso && (
        <p role="status" className="mt-4 rounded-xl bg-cielo px-4 py-3 text-center text-sm">
          {aviso}
        </p>
      )}

      {premios !== null && premios.length > 0 && (
        <p className="mt-6 text-xs leading-relaxed text-gris">
          Todo canje requiere la aprobación de tu supervisor y queda registrado con fecha, premio y
          puntos descontados.
        </p>
      )}
    </div>
  );
}
